import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { Box, Button } from '@mui/material';
import { Add, CopyAllRounded } from '@mui/icons-material';
import { FC, useMemo, useState } from 'react';
import { Table, Pagination, NeighborhoodDropdown, Loader } from '../../../../components';
import { INeighborhoodOption, IResponse } from '../../../../models';
import { getNeighborhoodOptions, createNeighborhoodCopySeries } from '../../../../fetch';
import { SeriesDetailModal } from './series-details-modal';
import { useHistory } from 'react-router-dom';

interface INeighborhoodOptionProps {
  neighborhoodId: string | number;
}

export const OptionsList: FC<INeighborhoodOptionProps> = ({ neighborhoodId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const history = useHistory();

  const {
    isLoading: isLoadingNeighborhoodOptions,
    data: neighborhoodOptionsData,
    refetch: refetchNeighborhoodOptions
  } = useQuery<IResponse<INeighborhoodOption[]>, Error>(
    ['neighborhoodsOptions', neighborhoodId, page, perPage],
    () =>
      getNeighborhoodOptions(neighborhoodId, {
        page: page + 1,
        perPage
      }),
    {
      enabled: neighborhoodId !== 'add',
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const neighborhoodOptions = useMemo(
    () => neighborhoodOptionsData?.records ?? [],
    [neighborhoodOptionsData]
  );

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'planStyleName'
      },
      {
        Header: 'Plan Style',
        accessor: 'planStyle'
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INeighborhoodOption } };
        }) => {
          return (
            <Box display="flex">
              <Button
                color="primary"
                onClick={() => {
                  history.push(
                    `/development/neighborhoods/${neighborhoodId}/options/${original.neighborhoodSeriesId}`
                  );
                }}
              >
                Details
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodOptions]);
  return (
    <>
      {isCopying && <Loader type="overlay" position="centered" />}
      <Box mt={1}>
        <Box
          display="flex"
          flexDirection={{
            xs: 'column',
            sm: 'row'
          }}
          mb={1}
        >
          <Box flex={1}>
            <Box
              display="flex"
              width={{
                xs: '100%',
                sm: '22rem',
                lg: '30rem'
              }}
            >
              <NeighborhoodDropdown
                inputLabel="Copy All From"
                value={selectedNeighborhoodId}
                handleChange={value => {
                  setSelectedNeighborhoodId(value);
                }}
              />
              <Button
                startIcon={<CopyAllRounded />}
                color="primary"
                variant="contained"
                onClick={async () => {
                  try {
                    setIsCopying(true);
                    await createNeighborhoodCopySeries({
                      sourceNeighborhoodId: Number(selectedNeighborhoodId),
                      destinationNeighborhoodId: Number(neighborhoodId)
                    });
                    setSelectedNeighborhoodId('');
                    enqueueSnackbar(`Success!`, {
                      variant: 'success'
                    });
                    refetchNeighborhoodOptions();
                  } catch (error) {
                    enqueueSnackbar(error?.Detail ?? `Error, please try again.`, {
                      variant: 'error'
                    });
                  } finally {
                    setIsCopying(false);
                  }
                }}
                disabled={!selectedNeighborhoodId || isCopying}
              >
                Copy
              </Button>
            </Box>
          </Box>
          <Box
            margin={{
              xs: '1rem 0 0 0',
              sm: '0 0 0 1rem'
            }}
          >
            <Button
              startIcon={<Add />}
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto'
                }
              }}
              color="primary"
              variant="contained"
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              Add New Series
            </Button>
          </Box>
        </Box>
        <Table
          columns={columns}
          data={neighborhoodOptions}
          isLoading={isLoadingNeighborhoodOptions}
          hidePagination
        />
        {!isLoadingNeighborhoodOptions && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Box>
      <SeriesDetailModal
        open={isModalOpen}
        modalIsLoading={isLoadingNeighborhoodOptions}
        onClose={() => setIsModalOpen(false)}
        currentSeries={null}
        fetchSeries={() => refetchNeighborhoodOptions()}
        neighborhoodId={neighborhoodId}
      />
    </>
  );
};
